/**
 * Admin settings for the admin page: read every setting with who saved it, and
 * save one. Admin auth and delegation to `~/lib/server/admin-settings`. Coaches
 * read their part through `~/lib/functions/calls-and-links`.
 *
 * Aliases: admin settings api, calls and links admin api
 */

import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";

import { permissionMiddleware } from "~/lib/permission-middleware";
import { saveAdminSettingInputSchema } from "~/lib/shared/admin-settings";

// Type-only re-export, erased at build, so a route component never imports
// from `~/lib/server`.
export type {
  AdminSettingsSnapshot,
  SavedAdminSetting,
} from "~/lib/server/admin-settings";

export const adminSettingsKeys = {
  admin: ["admin-settings"] as const,
};

export const getAdminSettingsFn = createServerFn({ method: "GET" })
  .middleware([permissionMiddleware("calls-and-links:read")])
  .handler(async () => {
    const { getAdminSettings } = await import("~/lib/server/admin-settings");
    return getAdminSettings();
  });

export const saveAdminSettingFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("calls-and-links:write")])
  .validator(saveAdminSettingInputSchema)
  .handler(async ({ data, context }) => {
    const { saveAdminSetting } = await import("~/lib/server/admin-settings");
    await saveAdminSetting(data, context.user.id);
  });

export function adminSettingsQueryOptions() {
  return queryOptions({
    queryKey: adminSettingsKeys.admin,
    queryFn: () => getAdminSettingsFn(),
  });
}
